import { isUndefined } from 'lodash-es';
import * as t from 'io-ts';
import { filterArray } from '@bayer-int/portletce-shared';

import type { SelectClearableDefinition } from 'header/types';

import type { Specification } from '../../specification';
import { define, createSpecSettingsInterface } from './_common';

const SmartViewValueC = t.type({
    key: t.string,
    label: t.string,
    shortLabel: t.string,
});

const AggregationsC = t.intersection([
    t.type({
        smartViewTag: t.string,
        name: t.string,
        items: t.array(SmartViewValueC),
    }),
    t.partial({
        defaultAggregationItems: t.array(t.string),
    }),
]);

export type SmartViewValue = t.TypeOf<typeof SmartViewValueC>;
export type Aggregations = t.TypeOf<typeof AggregationsC>;

export interface SmartViewSettingDefinition extends SelectClearableDefinition {
    key: 'SMART_VIEW';
    options: Array<SmartViewValue>;
}

const definition: SmartViewSettingDefinition = {
    key: 'SMART_VIEW',
    type: 'SelectClearable',
    label: 'Smart view',
    value: '',
    options: [],
    scope: 'designer',
};

const specSettingsInterface = createSpecSettingsInterface(definition, t.union([t.string, t.null]));

export const codec = specSettingsInterface.input;

export const getAggregationsFromSpec = (spec: Specification): Aggregations[] => {
    const aggregations = 'AGGREGATIONS';
    const aggregationsView =
        spec.views.find(view => view.id === spec.defaultViewId) || spec.views[0];
    if (!aggregationsView) return [];

    const aggregationsSignal = aggregationsView.signals?.find(
        signal => signal.name === aggregations,
    );

    if (!aggregationsSignal?.value) return [];

    return filterArray(AggregationsC)(aggregationsSignal.value);
};

export const SmartViewSettingDefinition = define({
    key: 'SMART_VIEW',
    label: 'smartView',
    definition,
    fromSetting: settings => specSettingsInterface.extractValue(settings.smartView),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (settings, spec) => {
        if (isUndefined(settings.smartView)) return undefined;

        const checkedAggregations = getAggregationsFromSpec(spec);

        const options = checkedAggregations.map(aggregation => ({
            key: aggregation.smartViewTag,
            label: `${aggregation.smartViewTag} (${aggregation.name})`,
            shortLabel: aggregation.smartViewTag,
        }));
        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.smartView),
            options,
        };
    },
});
